import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Search, Heart, Sparkles, Baby, Brain, Bone, Stethoscope, Eye, Smile, Calendar, Bell, Video } from "lucide-react";

const specIcons = { Cardiology: Heart, Dermatology: Sparkles, Pediatrics: Baby, Neurology: Brain, Orthopedics: Bone, "General Physician": Stethoscope, Ophthalmology: Eye, Dentistry: Smile, Psychiatry: Smile, Gynecology: Sparkles };

export default function PatientDashboard() {
  const { user } = useAuth();
  const [next, setNext] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [a, n, s] = await Promise.all([
        api.get("/appointments", { params: { status: "confirmed" } }),
        api.get("/notifications"),
        api.get("/specialties"),
      ]);
      const upcoming = a.data.filter((x) => x.date >= new Date().toISOString().slice(0, 10));
      upcoming.sort((x, y) => (x.date + x.start_time > y.date + y.start_time ? 1 : -1));
      setNext(upcoming[0] || null);
      setNotifs(n.data.filter((x) => !x.read).slice(0, 3));
      setSpecialties(s.data);
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-slate-500">Welcome back</p>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="welcome-name">{user?.name?.split(" ")[0] || "there"} 👋</h1>
      </div>

      {/* Search hero */}
      <div className="doccare-card p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-none">
        <h2 className="text-xl font-bold">Find the right doctor</h2>
        <p className="text-slate-300 text-sm mt-1">Search by name, specialty or clinic.</p>
        <form onSubmit={(e) => { e.preventDefault(); window.location.href = `/doctors?q=${encodeURIComponent(q)}`; }} className="mt-4 flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Cardiologist near me" className="pl-9 rounded-xl h-11 bg-white text-slate-900" data-testid="dash-search" />
          </div>
          <Button type="submit" className="rounded-xl h-11 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="dash-search-btn">Search</Button>
        </form>
      </div>

      {/* Specialty grid */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Browse specialties</h2>
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {specialties.slice(0, 10).map((s) => {
            const Icon = specIcons[s.name] || Stethoscope;
            return (
              <Link key={s.id} to={`/doctors?specialty=${encodeURIComponent(s.name)}`} className="doccare-card p-3 flex flex-col items-center gap-2 hover-lift text-center" data-testid={`spec-${s.name}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center"><Icon className="w-5 h-5" /></div>
                <span className="text-[11px] font-medium text-slate-700 leading-tight">{s.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Upcoming + notifications */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="doccare-card p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Calendar className="w-4 h-4" /> Next appointment</h3>
            <Link to="/patient/appointments" className="text-xs text-emerald-700 font-semibold">View all</Link>
          </div>
          {next ? (
            <div className="mt-4 flex items-center gap-4">
              <img src={next.doctor.photo} alt="" className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{next.doctor.name}</p>
                <p className="text-sm text-emerald-700">{next.doctor.specialty}</p>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                  {next.date} · {next.start_time}
                  {next.consultation_type === "video" && <span className="inline-flex items-center gap-1 text-blue-600"><Video className="w-3 h-3" /> Video</span>}
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">No upcoming appointments. <Link to="/doctors" className="text-emerald-700 font-semibold">Book one</Link>.</p>
          )}
        </div>

        <div className="doccare-card p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4" /> Recent alerts</h3>
            <Link to="/patient/notifications" className="text-xs text-emerald-700 font-semibold">View all</Link>
          </div>
          <div className="mt-4 space-y-3">
            {notifs.length === 0 && <p className="text-sm text-slate-500">You're all caught up.</p>}
            {notifs.map((n) => (
              <div key={n.id} className="flex gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{n.title}</p>
                  <p className="text-slate-500 text-xs">{n.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
